import { AxiosError } from 'axios'
import { useRouter } from 'next/router'
import { destroyCookie } from 'nookies'
import React, { useEffect } from 'react'
import { useQuery, useQueryClient } from 'react-query'

import { User } from '@/generated/api/@types'
import { client } from '@/src/config/clientApi'
import { unAuthorizedPages } from '@/src/const/path'
import { snackbarResource, userIdResource } from '@/src/const/resource'
import {
  isPathAllowedForUser,
  signInPathWithTaskInfo,
  UNAUTHORIZED_ACCESS_MESSAGE,
} from '@/src/utils/checkAuth'

type Props = {
  children: React.ReactElement
}

const TASKS_PATH_PREFIX = '/my/tasks/'

/**
 * ログイン状態と画面ごとの権限をブラウザ側で確認するガード。
 *
 * 以前は _app.getInitialProps (サーバーサイド) で API を呼んで確認していたが、
 * ALB の IP 制限により Amplify のサーバーから API へ到達できないため、ブラウザ側に移した。
 * ユーザー情報の取得が終わり、表示が許可されるまで子要素は描画しない。
 */
const MwAuthGuard: React.FC<Props> = ({ children }) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const isPublicPage = unAuthorizedPages.includes(router.pathname)

  const { data: user, isError } = useQuery<User, AxiosError>(
    userIdResource.key,
    userIdResource.fetcher,
    { enabled: !isPublicPage }
  )

  const isAllowed = !!user && isPathAllowedForUser(router.pathname, user)

  useEffect(() => {
    if (isPublicPage) return

    if (isError) {
      // トークンが無効 (期限切れなど)。業務画面からの場合は再ログイン後に同じ画面へ戻れるようにする
      const taskInfo = router.asPath.startsWith(TASKS_PATH_PREFIX)
        ? router.asPath.slice(TASKS_PATH_PREFIX.length)
        : undefined
      destroyCookie(null, 'token', { path: '/' })
      client.setToken('')
      router.replace(signInPathWithTaskInfo(taskInfo))
      return
    }

    if (user && !isAllowed) {
      queryClient.setQueryData(snackbarResource.key, {
        severity: 'error',
        message: UNAUTHORIZED_ACCESS_MESSAGE,
      })
      router.replace('/')
    }
  }, [isAllowed, isError, isPublicPage, queryClient, router, user])

  if (isPublicPage) return children
  if (!isAllowed) return null
  return children
}

export default MwAuthGuard
